































































































import { Component, Vue } from "vue-property-decorator";
import type { NavSection } from "@/config/host-feature-config";
import {
  isNavChildHidden,
  isNavSectionHidden,
  getNavSingleChild,
  getActiveGuideProfile,
} from "@/services/featureConfig";

interface FaqLeaf {
  key: string;
  routeName?: string;
}

interface TocItem {
  type: "item";
  key: string;
  num: string;
  title: string;
  desc: string;
}

interface TocLeaf {
  key: string;
  num: string;
  title: string;
  desc: string;
}

interface TocGroup {
  type: "group";
  num: string;
  label: string;
  items: TocLeaf[];
}

type TocNode = TocItem | TocGroup;

@Component({})
class FAQ extends Vue {
  /** Плоские пункты navbar (без дропбокса) с руководствами */
  private flatLeaves: FaqLeaf[] = [
    { key: "servers", routeName: "AdministrationServers" },
    { key: "room-service", routeName: "RoomService" },
    { key: "complex-menu", routeName: "ComplexMenu" },
    { key: "timetable", routeName: "Timetable" },
    { key: "instructions", routeName: "Instructions" },
    { key: "device-setup" },
  ];

  /** Раздел «Администрирование» */
  private adminLeaves: FaqLeaf[] = [
    { key: "system-config", routeName: "FiloxeniaConfiguration" },
    { key: "users", routeName: "AdministrationUsers" },
  ];

  /** Дропбокс «Маршруты» */
  private routesLeaves: FaqLeaf[] = [
    { key: "routes-v2", routeName: "RouteInfoV2Page" },
  ];

  /** Дропбокс «Карта» */
  private mapLeaves: FaqLeaf[] = [{ key: "wiki", routeName: "WikiList" }];

  /** Дропбокс «Сервисы» */
  private servicesLeaves: FaqLeaf[] = [
    { key: "menu", routeName: "MenuList" },
    { key: "excursions", routeName: "ExcursionList" },
  ];

  /** Дропбокс «Отель» */
  private hotelLeaves: FaqLeaf[] = [
    { key: "information", routeName: "HotelInfoList" },
    { key: "staff", routeName: "StaffPage" },
    { key: "hotel", routeName: "HotelAdministration" },
    { key: "guests", routeName: "GuestList" },
    { key: "restorants", routeName: "RestorantsConstructior" },
  ];

  /** Дропбокс «Медиа» */
  private mediaLeaves: FaqLeaf[] = [
    { key: "channels", routeName: "ChannelsList" },
    { key: "audio", routeName: "AudioList" },
    { key: "audiobooks", routeName: "AudiobooksList" },
    { key: "cinema", routeName: "CinemaList" },
    { key: "gallery", routeName: "GalleryPage" },
  ];

  questions: string[] = ["formats", "savePdf", "where", "languages", "missing"];

  get toc(): TocNode[] {
    const nodes: TocNode[] = [];
    let counter = 0;

    const pushItem = (leaf: FaqLeaf): void => {
      counter += 1;
      nodes.push(this.toItem(leaf, String(counter)));
    };

    const pushGroup = (
      labelKey: string,
      leaves: FaqLeaf[],
      section: NavSection
    ): void => {
      const visible = leaves.filter(
        (l) => !isNavChildHidden(section, l.routeName as string)
      );
      if (!visible.length) return;
      counter += 1;
      const groupNum = counter;
      nodes.push({
        type: "group",
        num: String(groupNum),
        label: String(this.$t(labelKey)),
        items: visible.map((l, i) => this.toItem(l, `${groupNum}.${i + 1}`)),
      });
    };

    // Плоские пункты: Управление, Обслуживание, Комплексное меню
    this.flatLeaves.forEach((leaf) => {
      if (leaf.key === "room-service") {
        if (!this.$getRight("getRoomServiceRequests")) return;
      }
      if (leaf.key === "timetable") {
        if (isNavSectionHidden("timetable")) return;
      }
      if (leaf.key === "instructions") {
        if (!this.$getRight("getSections")) return;
      }
      pushItem(leaf);
    });

    // Администрирование: конфигурация системы и пользователи
    pushGroup(
      "faqPage.groupAdministration",
      this.adminLeaves,
      "administration"
    );

    // Маршруты: singleChild → плоский пункт, иначе дропбокс
    const routesSingle = getNavSingleChild("routes");
    if (routesSingle) {
      const leaf = this.routesLeaves.find((l) => l.routeName === routesSingle);
      if (leaf) pushItem(leaf);
    } else {
      pushGroup("nav.RouteInfoV2Page", this.routesLeaves, "routes");
    }

    // Карта: показывается, если не скрыта секция целиком
    if (!isNavSectionHidden("map")) {
      pushGroup("nav.MapPage", this.mapLeaves, "map");
    }

    // Сервисы: singleChild → плоский пункт, иначе дропбокс
    const servicesSingle = getNavSingleChild("services");
    if (servicesSingle) {
      const leaf = this.servicesLeaves.find(
        (l) => l.routeName === servicesSingle
      );
      if (leaf) pushItem(leaf);
    } else {
      pushGroup("nav.Services", this.servicesLeaves, "services");
    }

    // Отель: всегда дропбокс
    pushGroup("nav.Hotel", this.hotelLeaves, "hotel");

    // Медиа: всегда дропбокс
    pushGroup("nav.Media", this.mediaLeaves, "media");

    return nodes;
  }

  private toItem(leaf: FaqLeaf, num: string): TocItem {
    const profile = this.activeProfile;
    const baseDescKey = `faqPage.guides.${leaf.key}.desc`;
    const profileDescKey = `${baseDescKey}_${profile}`;
    const descKey =
      profile && this.$te(profileDescKey) ? profileDescKey : baseDescKey;
    return {
      type: "item",
      key: leaf.key,
      num,
      title: String(this.$t(`faqPage.guides.${leaf.key}.title`)),
      desc: String(this.$t(descKey)),
    };
  }

  get base(): string {
    return process.env.BASE_URL || "/";
  }

  get activeProfile(): string {
    return getActiveGuideProfile();
  }

  guideHref(key: string): string {
    const url = `${this.base}docs/${key}/guide.html`;
    return this.activeProfile ? `${url}?profile=${this.activeProfile}` : url;
  }
}
export default FAQ;
